import {Component, OnInit, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {DatePipe} from '@angular/common';
import {ResponseModel} from '../../../DataModels/response-model';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';

@Component({
  selector: 'app-timesheet-form',
  templateUrl: './timesheet-form.component.html',
  styleUrls: ['./timesheet-form.component.css'],
  standalone: false
})
export class TimesheetFormComponent implements OnInit {
  TimesheetForm: UntypedFormGroup;
  vm: any = {
    title: $localize`:Timesheet Form@@timesheetForm:Timesheet Form`,
    item: {},
    buttonsDisabled: false,
    Preferences: [],
    mainListPage: '/timesheet/timesheet-list',
    formInitiated: false,
    employees: [],
    timeEntries: [],
    timeEntriesLoaded: false,
  };

  submitted = false;
  timesheetId = '';
  isSaveNew = false;
  showTimeEntryModal = false;
  editingEntryId: string | null = null;

  // Open pay period bounds (from Gusto)
  periodStart: NgbDateStruct | null = null;
  periodEnd: NgbDateStruct | null = null;
  periodStartDisplay = '';
  periodEndDisplay = '';
  openPeriodLoaded = false;
  payPeriods: any[] = [];
  selectedPeriodIndex: number | null = null;
  runPayrollBy = '';

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: false,
    saveClose: true,
    print: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: StorageService,
    private tableDataService: DataService,
    private notification: BsToastService,
    private datePipe: DatePipe,
    private titleService: Title,
    private formBuilder: UntypedFormBuilder,
    private nav: NavigationService,
    private confirmDialogService: ConfirmDialogService,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.vm.title);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.formInit();
    this.loadLookups();
    this.loadOpenPayPeriod();
    this.dataInit();
  }

  loadOpenPayPeriod() {
    this.tableDataService.getData('Gusto/open-pay-period').subscribe({
      next: (result: any) => {
        if (result?.pay_periods?.length) {
          this.payPeriods = result.pay_periods;
        }
        this.openPeriodLoaded = true;
      },
      error: () => {
        this.openPeriodLoaded = true;
      },
    });
  }

  onPeriodSelected(index: number | null) {
    if (index === null || index === undefined || !this.payPeriods[index]) {
      this.periodStart = null;
      this.periodEnd = null;
      this.periodStartDisplay = '';
      this.periodEndDisplay = '';
      this.runPayrollBy = '';
      this.TimesheetForm.controls.item_pay_period_start.setValue('');
      this.TimesheetForm.controls.item_pay_period_end.setValue('');
      return;
    }
    const p = this.payPeriods[index];
    const [sy, sm, sd] = (p.start_date as string).split('-').map(Number);
    const [ey, em, ed] = (p.end_date as string).split('-').map(Number);
    this.periodStart = {year: sy, month: sm, day: sd};
    this.periodEnd = {year: ey, month: em, day: ed};
    this.periodStartDisplay = `${String(sm).padStart(2, '0')}/${String(sd).padStart(2, '0')}/${sy}`;
    this.periodEndDisplay = `${String(em).padStart(2, '0')}/${String(ed).padStart(2, '0')}/${ey}`;
    const [ry, rm, rd] = (p.run_payroll_by as string).split('-').map(Number);
    this.runPayrollBy = `${String(rm).padStart(2, '0')}/${String(rd).padStart(2, '0')}/${ry}`;
    this.TimesheetForm.controls.item_pay_period_start.setValue(this.periodStartDisplay);
    this.TimesheetForm.controls.item_pay_period_end.setValue(this.periodEndDisplay);

    if (!this.vm.item?.id && this.TimesheetForm.controls.item_employee_id.value) {
      this.onSave(false);
    }
  }

  formatPeriodOption(p: any): string {
    const [sy, sm, sd] = (p.start_date as string).split('-').map(Number);
    const [ey, em, ed] = (p.end_date as string).split('-').map(Number);
    return `${String(sm).padStart(2, '0')}/${String(sd).padStart(2, '0')}/${sy} – ${String(em).padStart(2, '0')}/${String(ed).padStart(2, '0')}/${ey}`;
  }

  loadLookups() {
    this.tableDataService.getApiListData('employee', '?pageNumber=0&pageSize=999999&state=active').subscribe({
      next: (result: ResponseModel) => {
        this.vm.employees = result.Data;
      },
      error: (err) => console.error(err),
    });
  }

  dataInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.timesheetId = params.id;
      if (this.timesheetId !== undefined) {
        this.tableDataService.getData('pr_timesheet', this.timesheetId).subscribe({
          next: (result: any) => {
            this.vm.item = result;
            this.vm.formInitiated = true;
            this.vm.item.pay_period_start = this.datePipe.transform(this.vm.item.pay_period_start, 'MM/dd/yyyy');
            this.vm.item.pay_period_end = this.datePipe.transform(this.vm.item.pay_period_end, 'MM/dd/yyyy');
            this.setValues();
            this.loadTimeEntries();
          },
          error: (err) => {
            console.error(err);
            this.notification.showErrorToast($localize`:Error loading timesheet@@errorLoadingTimesheet:Error loading timesheet`);
          },
        });
      }
    });
  }

  loadTimeEntries() {
    if (!this.timesheetId) {
      return;
    }
    this.tableDataService.getApiListData('pr_time_entry', '?timesheet_id=' + this.timesheetId + '&pageNumber=0&pageSize=999999').subscribe({
      next: (result: ResponseModel) => {
        this.vm.timeEntries = result.Data;
        this.vm.timeEntriesLoaded = true;
      },
      error: (err) => console.error(err),
    });
  }

  get f() {
    return this.TimesheetForm.controls;
  }

  get entryTypeSummary(): { type: string; hours: number }[] {
    const entries: any[] = this.vm.timeEntries ?? [];
    const map = new Map<string, number>();
    for (const e of entries) {
      const type = e.entry_type ?? 'regular';
      map.set(type, (map.get(type) ?? 0) + (e.hours ?? 0));
    }
    return Array.from(map.entries()).map(([type, hours]) => ({type, hours}));
  }

  get totalHours(): number {
    return (this.vm.timeEntries ?? []).reduce((sum: number, e: any) => sum + (e.hours ?? 0), 0);
  }

  formInit() {
    this.TimesheetForm = this.formBuilder.group({
      item_employee_id: new UntypedFormControl('', Validators.required),
      item_pay_period_start: new UntypedFormControl('', Validators.required),
      item_pay_period_end: new UntypedFormControl('', Validators.required),
    });
  }

  setValues() {
    this.TimesheetForm.controls.item_employee_id.setValue(this.vm.item.employee_id);
    this.TimesheetForm.controls.item_employee_id.disable();
    this.TimesheetForm.controls.item_pay_period_start.setValue(this.vm.item.pay_period_start);
    this.TimesheetForm.controls.item_pay_period_end.setValue(this.vm.item.pay_period_end);
    // Populate display values for read-only period display in edit mode
    this.periodStartDisplay = this.vm.item.pay_period_start ?? '';
    this.periodEndDisplay = this.vm.item.pay_period_end ?? '';
    // Try to match the loaded period against available pay periods to show run_payroll_by
    if (this.payPeriods.length && this.vm.item.pay_period_start) {
      const idx = this.payPeriods.findIndex((p: any) => {
        const [sy, sm, sd] = (p.start_date as string).split('-').map(Number);
        const display = `${String(sm).padStart(2, '0')}/${String(sd).padStart(2, '0')}/${sy}`;
        return this.vm.item.pay_period_start.startsWith(display) || this.vm.item.pay_period_start.includes(p.start_date);
      });
      if (idx >= 0) {
        const [ry, rm, rd] = (this.payPeriods[idx].run_payroll_by as string).split('-').map(Number);
        this.runPayrollBy = `${String(rm).padStart(2, '0')}/${String(rd).padStart(2, '0')}/${ry}`;
      }
    }
  }

  setupValuesForSave() {
    this.vm.item.employee_id = this.TimesheetForm.controls.item_employee_id.getRawValue() ?? this.vm.item.employee_id;
    this.vm.item.pay_period_start = new Date(this.TimesheetForm.controls.item_pay_period_start.value).toISOString();
    this.vm.item.pay_period_end = new Date(this.TimesheetForm.controls.item_pay_period_end.value).toISOString();
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'approved':
        return 'bg-success';
      case 'submitted':
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  }

  saveClose() {
    this.onSave(true);
  }

  resetForm() {
    this.resetToDefaults();
  }

  onClose() {
    this.nav.forceNextBackToFallback();
    this.nav.backOr(this.vm.mainListPage);
  }

  deleteItem() {
    if (!this.vm.item.id) {
      return false;
    }
    const messages: string[] = [
      $localize`:Confirmation for Delete Timesheet@@confirmDeleteTimesheet:Are you sure you want to delete this timesheet?`,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.vm.buttonsDisabled = true;
        this.tableDataService.deleteDataById('pr_timesheet', this.vm.item.id).subscribe({
          next: () => {
            this.notification.showSuccessToast($localize`:Deleted@@deleted:Deleted`);
            this.onClose();
          },
          error: (err) => {
            this.vm.buttonsDisabled = false;
            console.error(err);
            this.notification.showErrorToast($localize`:Error deleting timesheet@@errorDeletingTimesheet:Error deleting timesheet`);
          },
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      }
    );
    return true;
  }

  submitTimesheet() {
    if (!this.vm.item.id || this.vm.buttonsDisabled) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.put('pr_timesheet/' + this.vm.item.id + '/submit', '', {}).subscribe({
      next: () => {
        this.vm.item.status = 'submitted';
        this.notification.showSuccessToast($localize`:Submitted@@submitted:Submitted`);
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        this.vm.buttonsDisabled = false;
        console.error(err);
        this.notification.showErrorToast($localize`:Error submitting timesheet@@errorSubmittingTimesheet:Error submitting timesheet`);
      },
    });
  }

  approveTimesheet() {
    if (!this.vm.item.id || this.vm.buttonsDisabled) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.put('pr_timesheet/' + this.vm.item.id + '/approve', '', {}).subscribe({
      next: () => {
        this.vm.item.status = 'approved';
        this.notification.showSuccessToast($localize`:Approved@@approved:Approved`);
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        this.vm.buttonsDisabled = false;
        console.error(err);
        this.notification.showErrorToast($localize`:Error approving timesheet@@errorApprovingTimesheet:Error approving timesheet`);
      },
    });
  }

  pushToGusto() {
    if (!this.vm.item.id || this.vm.buttonsDisabled) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.post('Gusto/push-timesheet/' + this.vm.item.id, {}).subscribe({
      next: (result: any) => {
        this.vm.item.gusto_timesheet_id = result.gusto_timesheet_id;
        this.notification.showSuccessToast($localize`:Pushed to Gusto@@pushedToGusto:Pushed to Gusto`);
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        this.vm.buttonsDisabled = false;
        console.error(err);
        this.notification.showErrorToast($localize`:Error pushing to Gusto@@errorPushingToGusto:Error pushing to Gusto`);
      },
    });
  }

  isoToTime(iso: string | null): string {
    if (!iso) {
      return '';
    }
    try {
      const d = new Date(iso);
      const h = d.getHours();
      const m = d.getMinutes().toString().padStart(2, '0');
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${m} ${ampm}`;
    } catch {
      return '';
    }
  }

  openAddEntry() {
    this.editingEntryId = null;
    this.showTimeEntryModal = true;
  }

  openEditEntry(entryId: string) {
    this.editingEntryId = entryId;
    this.showTimeEntryModal = true;
  }

  onEntrySaved() {
    this.showTimeEntryModal = false;
    this.editingEntryId = null;
    this.loadTimeEntries();
  }

  onEntryModalClose() {
    this.showTimeEntryModal = false;
    this.editingEntryId = null;
  }

  deleteEntry(entryId: string) {
    this.tableDataService.deleteDataById('pr_time_entry', entryId).subscribe({
      next: () => {
        this.notification.showSuccessToast($localize`:Entry deleted@@entryDeleted:Entry deleted`);
        this.loadTimeEntries();
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast($localize`:Error deleting entry@@errorDeletingEntry:Error deleting entry`);
      },
    });
  }

  private resetToDefaults(): void {
    this.TimesheetForm.reset({
      item_employee_id: '',
      item_pay_period_start: '',
      item_pay_period_end: '',
    });
    this.vm.item = {};
    this.vm.timeEntries = [];
    this.submitted = false;
    this.vm.buttonsDisabled = false;
    this.timesheetId = '';
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    this.vm.buttonsDisabled = true;

    if (this.TimesheetForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors!@@formerrors:Form Errors!`);
      this.vm.buttonsDisabled = false;
      return false;
    }

    this.setupValuesForSave();

    if (!this.vm.item.id) {
      this.tableDataService.post('pr_timesheet', this.vm.item).subscribe({
        next: (item: any) => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          if (saveClose) {
            this.onClose();
          } else {
            this.vm.item = item;
            this.timesheetId = item.id;
            this.loadTimeEntries();
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {id: item.id},
              replaceUrl: true,
            });
          }
        },
        error: (error: any) => {
          this.vm.buttonsDisabled = false;
          console.error(error);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
          this.submitted = false;
        },
      });
    } else {
      this.tableDataService.patch('pr_timesheet', this.vm.item.id, this.vm.item)
        .then(() => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          if (saveClose) {
            this.onClose();
          }
        })
        .catch((error: any) => {
          this.vm.buttonsDisabled = false;
          console.error(error);
        })
        .finally(() => {
          this.vm.buttonsDisabled = false;
          this.submitted = false;
        });
    }
    return true;
  }
}
